import { useCallback, useEffect, useMemo, useState } from 'react';
import { getIn } from 'formik';
import { useDispatch } from 'react-redux';

import { DateHelper } from '../../../../utils';
import { datePickerFieldsNames } from '../../../../utils/helpers';

import { resetWindowOfTimeValidation } from '../../../../store/rides';
import { useLocation } from 'react-router-dom';

export const useDateTimeHandlers = ({
  validateForm,
  setFieldTouched,
  defaultValues,
  minDelayToStartRide,
  maxPickUpWindowOfTime,
  minimumRoundTrip,
  isUberRide = false,
}) => {
  const location = useLocation();
  const [pickUpDate, setPickUpDate] = useState(null);

  const [returnTimeDate, setReturnTimeDate] = useState(null);

  const [startPointOfRecurring, setStartPointOfRecurring] = useState(null);

  const [returnStartPoint, setReturnStartPoint] = useState(null);

  const [pickUpDateLowerLimit, setPickUpDateLowerLimit] = useState(null);
  const [returnTimeLowerLimit, setReturnTimeLowerLimit] = useState(null);

  const [appointmentDate, setAppointmentDate] = useState(null);
  const [isAppointmentDateSelected, setIsAppointmentDateSelected] = useState(false);

  const dispatch = useDispatch();

  const getFlexibleLatestPickupMax = useCallback(
    (earliestDate) =>
      isUberRide
        ? DateHelper.getUberFlexibleLatestPickupMax(earliestDate)
        : DateHelper.addDates(earliestDate, maxPickUpWindowOfTime),
    [isUberRide, maxPickUpWindowOfTime],
  );

  const getFlexibleDisabledTime = useCallback(
    (startDate, currentDate) => {
      const maxDate = getFlexibleLatestPickupMax(startDate);

      if (isUberRide) {
        return DateHelper.disabledTimeForFlexibleWindow({ currentDate, startDate, maxDate });
      }

      return DateHelper.disabledTimeForHigherLimit({ currentDate, startDate, maxDate });
    },
    [getFlexibleLatestPickupMax, isUberRide],
  );

  const disabledDateTime = useCallback(
    (minDate, currentValue) => (selectedDate) => {
      const dateForTime = selectedDate ?? currentValue;

      if (!dateForTime || !minDate) {
        return {
          disabledHours: () => DateHelper.getRange(0, 24),
          disabledMinutes: () => DateHelper.getRange(0, 60),
        };
      }

      const isTheSameDay = DateHelper.isTheSameDay(dateForTime, minDate);
      const minHour = DateHelper.getHoursNumber(minDate);
      const minMinute = DateHelper.getMinutesNumber(minDate);

      return {
        disabledHours: () =>
          isTheSameDay ? DateHelper.getRange(0, 24).splice(0, minHour) : [],
        disabledMinutes: (hour) =>
          isTheSameDay && hour === minHour ? DateHelper.getRange(0, minMinute) : [],
      };
    },
    [],
  );

  useEffect(() => {
    if (!pickUpDate) {
      setReturnTimeDate(null);
      setReturnTimeLowerLimit(null);
    }
    return;
  }, [pickUpDate]);

  const getDefaultValuesForDateTime = useCallback(
    (fieldName) => {
      const fieldValue = getIn(defaultValues, fieldName);
      if (!fieldValue?.datetime || !fieldValue?.timezone) {
        return null;
      }

      const zonedDate = DateHelper.utcToZonedTime(fieldValue.datetime, fieldValue.timezone);
      return DateHelper.isValidDateTime(zonedDate) ? zonedDate : null;
    },
    [defaultValues],
  );

  useEffect(() => {
    setPickUpDate(getDefaultValuesForDateTime(datePickerFieldsNames.pickUpDate.title));
    setReturnTimeDate(getDefaultValuesForDateTime(datePickerFieldsNames.returnTime.title));
    setStartPointOfRecurring(getDefaultValuesForDateTime(datePickerFieldsNames.startPoint.title));
    setReturnStartPoint(getDefaultValuesForDateTime(datePickerFieldsNames.returnStartPoint.title));
    setAppointmentDate(getDefaultValuesForDateTime(datePickerFieldsNames.appointmentDate.title));

    defaultValues?.isPickUpDateFlexible &&
      setPickUpDateLowerLimit(getDefaultValuesForDateTime(datePickerFieldsNames.pickUpDateLowerLimit.title));
    defaultValues?.isReturnDateFlexible &&
      setReturnTimeLowerLimit(getDefaultValuesForDateTime(datePickerFieldsNames.returnTimeLowerLimit.title));

    return;
  }, [defaultValues, getDefaultValuesForDateTime]);

  const handleUseWindowOfTime = useCallback(
    async ({ name, values, checked, setFieldValue }) => {
      await setFieldValue('isRecurring', false);
      await setFieldValue('recurrenceRule', undefined);

      if (name === 'isPickUpDateFlexible') {
        await setFieldValue('pickUpDateLowerLimit', undefined);
        await setFieldValue('pickUpDate.datetime', '');
        await setFieldValue('appointmentDate.datetime', '');
        await setPickUpDate(null);
        await setAppointmentDate(null);
        await setPickUpDateLowerLimit(null);
      } else if (name === 'isReturnDateFlexible') {
        await setFieldValue('returnTimeLowerLimit', undefined);
        await setFieldValue('returnTime.datetime', '');
        await setReturnTimeDate(null);
        await setReturnTimeLowerLimit(null);
      }
      await setFieldValue(name, checked);

      if (!checked && (!returnTimeLowerLimit || !pickUpDateLowerLimit)) {
        dispatch(resetWindowOfTimeValidation());
      }
      await validateForm();
    },
    [validateForm, returnTimeLowerLimit, pickUpDateLowerLimit, dispatch],
  );

  const handleChangeappointmentDate = async ({
    value,
    setFieldValue,
    values,
  }) => {
    if (value === null) {
      await setPickUpDate(null);
      await setPickUpDateLowerLimit(null);
      await setAppointmentDate(null);
      await setFieldValue(datePickerFieldsNames.appointmentDate.datetime, "");
      return await setFieldValue(
        datePickerFieldsNames.appointmentDate.datetime,
        ""
      );
    }

    const minDate = DateHelper.addDates(
      DateHelper.utcToZonedTime(Date.now(), values.appointmentDate.timezone),
      minDelayToStartRide
    );
    let datetime, pickupDateTimeValue;
    pickupDateTimeValue = DateHelper.isBefore(value, minDate) ? minDate : value;
    datetime = DateHelper.zonedTimeToUtcISOS(
      pickupDateTimeValue,
      values?.appointmentDate?.timezone
    );
    await setAppointmentDate(pickupDateTimeValue);
    await setFieldValue(
      datePickerFieldsNames.appointmentDate.datetime,
      datetime
    );
    await setFieldValue(
      datePickerFieldsNames.appointmentDate.timezone,
      values?.appointmentDate?.timezone
    );
    await validateForm();
  };

  const resolvePickUpDateTimeValue = useCallback(
    (value, values, { forceSnapTime = false, previousDate = pickUpDate } = {}) => {
      const minDate =
        pickUpDateLowerLimit ||
        DateHelper.addDates(
          DateHelper.utcToZonedTime(Date.now(), values.pickUpDate.timezone),
          minDelayToStartRide,
        );

      const disabledTime =
        values?.isPickUpDateFlexible && pickUpDateLowerLimit
          ? getFlexibleDisabledTime(pickUpDateLowerLimit, value)
          : disabledDateTime(minDate, value)(value);

      const dayChanged = !previousDate || !DateHelper.isTheSameDay(previousDate, value);
      const snapTimeOnDayChange = forceSnapTime || dayChanged;

      let resolved = snapTimeOnDayChange
        ? DateHelper.getFirstAvailableDateTime(value, disabledTime)
        : value;

      if (DateHelper.isBefore(resolved, minDate)) {
        resolved = DateHelper.isTheSameDay(resolved, minDate)
          ? minDate
          : DateHelper.getFirstAvailableDateTime(value, disabledTime);
      }

      const flexibleEarliest = values?.isPickUpDateFlexible ? pickUpDateLowerLimit || minDate : null;
      const flexibleMax = flexibleEarliest ? getFlexibleLatestPickupMax(flexibleEarliest) : null;

      if (flexibleMax && DateHelper.isAfter(resolved, flexibleMax)) {
        resolved = flexibleMax;
      }

      return resolved;
    },
    [
      pickUpDate,
      pickUpDateLowerLimit,
      minDelayToStartRide,
      getFlexibleLatestPickupMax,
      getFlexibleDisabledTime,
      disabledDateTime,
    ],
  );

  const applyPickUpDateTime = useCallback(
    async ({ value, setFieldValue, values, forceSnapTime = false }) => {
      const query = new URLSearchParams(location.search);
      const editSeries = query.get('mode');

      if (value === null) {
        await setPickUpDate(null);

        return await setFieldValue(datePickerFieldsNames.pickUpDate.datetime, '');
      }
      await setFieldValue('appointmentDate.datetime', '');
      await setAppointmentDate(null);

      const allowedpickupDateTimeValue = resolvePickUpDateTimeValue(value, values, { forceSnapTime });

      if (!allowedpickupDateTimeValue || !DateHelper.isValidDateTime(allowedpickupDateTimeValue)) {
        return;
      }

      const datetime = DateHelper.zonedTimeToUtcISOS(allowedpickupDateTimeValue, values?.pickUpDate?.timezone);
      if (!datetime) {
        return;
      }

      await setPickUpDate(allowedpickupDateTimeValue);
      await setFieldValue(datePickerFieldsNames.pickUpDate.datetime, datetime);

      if (values?.isRoundtrip) {
        await setReturnTimeDate(null);
        await setFieldValue(datePickerFieldsNames.returnTime.datetime, undefined);
      }

      if (values?.isRecurring && !editSeries) {
        await setStartPointOfRecurring(allowedpickupDateTimeValue);
        await setReturnStartPoint(null);
        await setFieldValue(datePickerFieldsNames.startPoint.datetime, datetime);
      }

      await validateForm();
    },
    [resolvePickUpDateTimeValue, validateForm, location.search],
  );

  const handlePickUpDateTime = applyPickUpDateTime;

  const handlePickUpDateOnSelect = useCallback(
    async (selectPayload) => {
      await handlePickUpDateTime(selectPayload);
    },
    [handlePickUpDateTime],
  );

  const resolvePickUpLowerLimitValue = useCallback(
    (value, values, { forceSnapTime = false, previousDate = pickUpDateLowerLimit } = {}) => {
      const minDate = DateHelper.addDates(
        DateHelper.utcToZonedTime(Date.now(), values.pickUpDate.timezone),
        minDelayToStartRide,
      );
      const disabledTime = disabledDateTime(minDate, value)(value);
      const dayChanged = !previousDate || !DateHelper.isTheSameDay(previousDate, value);
      const snapTimeOnDayChange = forceSnapTime || dayChanged;

      let resolved = snapTimeOnDayChange
        ? DateHelper.getFirstAvailableDateTime(value, disabledTime)
        : value;

      if (DateHelper.isBefore(resolved, minDate)) {
        resolved = DateHelper.isTheSameDay(resolved, minDate)
          ? minDate
          : DateHelper.getFirstAvailableDateTime(value, disabledTime);
      }

      return resolved;
    },
    [pickUpDateLowerLimit, minDelayToStartRide, disabledDateTime],
  );

  const handleChangePickUpDateToLowerLimit = async ({ value, setFieldValue, values, forceSnapTime = false }) => {
    if (value === null) {
      await setPickUpDate(null);
      await setAppointmentDate(null);
      await setPickUpDateLowerLimit(null);
      await setFieldValue('appointmentDate.datetime', '');
      await setFieldValue(datePickerFieldsNames.pickUpDate.datetime, '');
      return await setFieldValue(datePickerFieldsNames.pickUpDateLowerLimit.datetime, '');
    }

    let datetime;
    const pickupDateTimeValue = resolvePickUpLowerLimitValue(value, values, { forceSnapTime });

    const latestPickupMax = getFlexibleLatestPickupMax(value);

    const selectedDateIsOutOfInterval = !DateHelper.isWithinInterval(pickUpDate, {
      start: value,
      end: latestPickupMax,
    });

    if (pickUpDate && selectedDateIsOutOfInterval) {
      await setPickUpDate(null);
      await setFieldValue(datePickerFieldsNames.pickUpDate.datetime, '');
      await setAppointmentDate(null);
      await setFieldValue('appointmentDate.datetime', '');
      await setPickUpDateLowerLimit(pickupDateTimeValue);
      return await setFieldValue(
        datePickerFieldsNames.pickUpDateLowerLimit.datetime,
        DateHelper.zonedTimeToUtcISOS(pickupDateTimeValue, values?.pickUpDate?.timezone),
      );
    }

    datetime = DateHelper.zonedTimeToUtcISOS(pickupDateTimeValue, values?.pickUpDate?.timezone);
    await setPickUpDateLowerLimit(pickupDateTimeValue);
    await setFieldValue(datePickerFieldsNames.pickUpDateLowerLimit.datetime, datetime);
    await setFieldValue(datePickerFieldsNames.pickUpDateLowerLimit.timezone, values?.pickUpDate?.timezone);
    await validateForm();
  };

  const handlePickUpLowerLimitOnSelect = async (selectPayload) => {
    await handleChangePickUpDateToLowerLimit(selectPayload);
  };

  const getReturnMinDate = useCallback(() => {
    if (returnTimeLowerLimit && DateHelper.isValidDateTime(returnTimeLowerLimit)) {
      return returnTimeLowerLimit;
    }
    if (!pickUpDate || !DateHelper.isValidDateTime(pickUpDate)) {
      return null;
    }
    const minDate = DateHelper.addDates(pickUpDate, minimumRoundTrip);
    return DateHelper.isValidDateTime(minDate) ? minDate : null;
  }, [pickUpDate, returnTimeLowerLimit, minimumRoundTrip]);

  const resolveReturnDateTimeValue = useCallback(
    (value, values, { forceSnapTime = false, previousDate = returnTimeDate } = {}) => {
      if (!value || !DateHelper.isValidDateTime(value)) {
        return value;
      }

      const minDate = getReturnMinDate();
      if (!minDate) {
        return value;
      }

      const disabledTime =
        values?.isReturnDateFlexible && returnTimeLowerLimit
          ? getFlexibleDisabledTime(returnTimeLowerLimit, value)
          : disabledDateTime(minDate, value)(value);

      const dayChanged = !previousDate || !DateHelper.isTheSameDay(previousDate, value);
      const snapTimeOnDayChange = forceSnapTime || dayChanged;

      let resolved = snapTimeOnDayChange
        ? DateHelper.getFirstAvailableDateTime(value, disabledTime)
        : value;

      if (DateHelper.isBefore(resolved, minDate)) {
        resolved = DateHelper.isTheSameDay(resolved, minDate)
          ? minDate
          : DateHelper.getFirstAvailableDateTime(value, disabledTime);
      }

      const flexibleEarliest = values?.isReturnDateFlexible ? returnTimeLowerLimit || minDate : null;
      const flexibleMax = flexibleEarliest ? getFlexibleLatestPickupMax(flexibleEarliest) : null;

      if (flexibleMax && DateHelper.isAfter(resolved, flexibleMax)) {
        resolved = flexibleMax;
      }

      return DateHelper.isValidDateTime(resolved) ? resolved : value;
    },
    [
      returnTimeDate,
      returnTimeLowerLimit,
      getReturnMinDate,
      getFlexibleDisabledTime,
      getFlexibleLatestPickupMax,
      disabledDateTime,
    ],
  );

  const applyReturnDateTime = useCallback(
    async ({ value, setFieldValue, values, forceSnapTime = false }) => {
      if (value === null) {
        await setReturnTimeDate(null);
        return await setFieldValue(datePickerFieldsNames.returnTime.datetime, '');
      }

      if (!getReturnMinDate() || !values?.returnTime?.timezone) {
        return;
      }

      const allowedpickupDateTimeValue = resolveReturnDateTimeValue(value, values, { forceSnapTime });

      const datetime = DateHelper.zonedTimeToUtcISOS(allowedpickupDateTimeValue, values?.returnTime?.timezone);

      if (!datetime) {
        return;
      }

      await setReturnTimeDate(allowedpickupDateTimeValue);
      await setReturnStartPoint(allowedpickupDateTimeValue);
      
      await setFieldValue(datePickerFieldsNames.returnTime.datetime, datetime);

      if (values?.isRecurring) {
        await setFieldValue(
          datePickerFieldsNames.returnStartPoint.datetime,
          datetime,
        );
      }

      await validateForm();
    },
    [ getReturnMinDate, resolveReturnDateTimeValue, validateForm ],
  );

  const handleReturnTime = applyReturnDateTime;

  const handleReturnTimeOnSelect = useCallback(
    async (selectPayload) => {
      
      await applyReturnDateTime({
        value: selectPayload?.value ?? selectPayload,
      });
    },
    [applyReturnDateTime],
  );

  const resolveReturnLowerLimitValue = useCallback(
    (value, values, { forceSnapTime = false, previousDate = returnTimeLowerLimit } = {}) => {
      if (!value || !DateHelper.isValidDateTime(value)) {
        return value;
      }

      if (!pickUpDate || !DateHelper.isValidDateTime(pickUpDate)) {
        return value;
      }

      const minDate = DateHelper.addDates(pickUpDate, minimumRoundTrip);
      if (!DateHelper.isValidDateTime(minDate)) {
        return value;
      }

      const disabledTime = disabledDateTime(minDate, value)(value);
      const dayChanged = !previousDate || !DateHelper.isTheSameDay(previousDate, value);
      const snapTimeOnDayChange = forceSnapTime || dayChanged;

      let resolved = snapTimeOnDayChange
        ? DateHelper.getFirstAvailableDateTime(value, disabledTime)
        : value;

      if (DateHelper.isBefore(resolved, minDate)) {
        resolved = DateHelper.isTheSameDay(resolved, minDate)
          ? minDate
          : DateHelper.getFirstAvailableDateTime(value, disabledTime);
      }

      return DateHelper.isValidDateTime(resolved) ? resolved : value;
    },
    [pickUpDate, minimumRoundTrip, returnTimeLowerLimit, disabledDateTime],
  );

  const handleReturnTimeLowerLimit = async ({ value, setFieldValue, values, forceSnapTime = false }) => {
    if (value === null) {
      await setReturnTimeDate(null);
      await setReturnTimeLowerLimit(null);
      await setFieldValue(datePickerFieldsNames.returnTime.datetime, '');
      return await setFieldValue(datePickerFieldsNames.returnTimeLowerLimit.datetime, '');
    }

    const latestReturnMax = getFlexibleLatestPickupMax(value);

    const selectedDateIsOutOfInterval = !DateHelper.isWithinInterval(returnTimeDate, {
      start: value,
      end: latestReturnMax,
    });

    let minDate, datetime, returnDateTimeValue;

    minDate = getReturnMinDate();
    if (!minDate) {
      await validateForm();
      return;
    }

    returnDateTimeValue = resolveReturnLowerLimitValue(value, values, { forceSnapTime });

    if (returnTimeDate && selectedDateIsOutOfInterval) {
      await setReturnTimeDate(null);
      await setFieldValue(datePickerFieldsNames.returnTime.datetime, '');
      if (!DateHelper.isValidDateTime(returnDateTimeValue) || !values?.returnTime?.timezone) {
        await validateForm();
        return;
      }
      const lowerLimitDatetime = DateHelper.zonedTimeToUtcISOS(
        returnDateTimeValue,
        values?.returnTime?.timezone,
      );
      if (!lowerLimitDatetime) {
        await validateForm();
        return;
      }
      await setReturnTimeLowerLimit(returnDateTimeValue);
      return await setFieldValue(
        datePickerFieldsNames.returnTimeLowerLimit.datetime,
        lowerLimitDatetime,
      );
    }

    if (
      minDate &&
      value &&
      DateHelper.isValidDateTime(returnDateTimeValue) &&
      values?.returnTime?.timezone
    ) {
      datetime = DateHelper.zonedTimeToUtcISOS(returnDateTimeValue, values?.returnTime?.timezone);
      if (!datetime) {
        await validateForm();
        return;
      }
      await setReturnTimeLowerLimit(returnDateTimeValue);
      await setFieldValue(datePickerFieldsNames.returnTimeLowerLimit.datetime, datetime);
      await setFieldValue(datePickerFieldsNames.returnTimeLowerLimit.timezone, values?.returnTime?.timezone);
    }
    await validateForm();
  };

  const handleReturnLowerLimitOnSelect = async (selectPayload) => {
    await handleReturnTimeLowerLimit(selectPayload);
  };

  const handleStartPointTime = useCallback(
    async ({ value, setFieldValue, values }) => {
      const minDate = DateHelper.addDates(pickUpDate, minimumRoundTrip);
      const datetime = DateHelper.zonedTimeToUtcISOS(
        DateHelper.compareDates(minDate, value) ? minDate : value,
        values?.recurrenceRule?.startPoint?.timezone,
      );
      const pickupDateTimeValue = DateHelper.compareDates(minDate, value) ? minDate : value;
      await setStartPointOfRecurring(pickupDateTimeValue);
      await setReturnStartPoint(null);
      await setFieldValue(datePickerFieldsNames.startPoint.datetime, datetime);

      if (values?.isRoundtrip) {
        await setFieldValue(
          datePickerFieldsNames.returnStartPoint.timezone,
          values?.recurrenceRule?.returnStartPoint?.timezone,
        );
        await setFieldValue(datePickerFieldsNames.returnStartPoint.datetime, undefined);
      }
      await validateForm();
    },
    [pickUpDate, validateForm, minimumRoundTrip],
  );

  const handleReturnStartPoint = useCallback(
    async ({ value, setFieldValue, values }) => {
      const minDate = DateHelper.addDates(startPointOfRecurring, minimumRoundTrip);

      const datetime = DateHelper.zonedTimeToUtcISOS(
        DateHelper.compareDates(minDate, value) ? minDate : value,
        values?.recurrenceRule?.returnStartPoint.timezone,
      );
      await setReturnStartPoint(DateHelper.compareDates(minDate, value) ? minDate : value);
      await setFieldValue(datePickerFieldsNames.returnStartPoint.datetime, datetime);
      await validateForm();
    },
    [startPointOfRecurring, validateForm, minimumRoundTrip],
  );

  const handleRoundTripCheckbox = useCallback(
    async ({ name, checked, values, setValues }) => {
      const copiedValues = { ...values };
      const returnTimeValue = defaultValues?.returnTime
        ? DateHelper.utcToZonedTime(defaultValues?.returnTime?.datetime, defaultValues?.returnTime?.timezone)
        : null;

      if (checked) {
        copiedValues.returnTime = { timezone: copiedValues?.pickUpDate?.timezone };
        if (values?.isRecurring && copiedValues.recurrenceRule) {
          copiedValues.recurrenceRule.returnStartPoint = {
            timezone: copiedValues?.pickUpDate?.timezone || '',
          };
        }

        setReturnTimeDate(null);
      } else {
        delete copiedValues.returnTime;
        if (values?.isRecurring && copiedValues?.recurrenceRule) {
          delete copiedValues?.recurrenceRule?.returnStartPoint;
        }
        if (values?.isReturnDateFlexible) {
          copiedValues.isReturnDateFlexible = false;
          delete copiedValues.returnTimeLowerLimit;
        }

        await setReturnTimeDate(returnTimeValue);
        await setReturnTimeLowerLimit(returnTimeValue);
        await setReturnStartPoint(returnTimeValue);
      }

      await setValues({ ...copiedValues, [name]: checked });
      await validateForm();
    },
    [defaultValues?.returnTime, validateForm],
  );

  const handleAppointmentDateCheckBox = useCallback(
    async ({ value, setFieldValue, checked, values }) => {
      setAppointmentDate(null);
      await setFieldValue('appointmentDate.datetime', '');

      if (checked) {
        setIsAppointmentDateSelected(true);
        await setFieldValue('appointmentDate.timezone', values?.pickUpDate?.timezone || '');

      } else {
        setIsAppointmentDateSelected(false);
      }

      return;
    }, [])


  const disabledDate = useCallback(
    (fieldName, minDate) => (current) => {


      if (
        fieldName === datePickerFieldsNames.pickUpDate.title ||
        fieldName === datePickerFieldsNames.startPoint.title
      ) {
        return DateHelper.isBefore(current, minDate) && !DateHelper.isTheSameDay(current, minDate);
      } else if (
        fieldName === datePickerFieldsNames.appointmentDate.title
      ) {
        return DateHelper.isBefore(current, minDate) && !DateHelper.isTheSameDay(current, minDate);
      }
      else if (fieldName === datePickerFieldsNames.returnTime.title && minDate) {
        return DateHelper.isBefore(current, minDate) && !DateHelper.isTheSameDay(current, minDate);
      } else if (fieldName === datePickerFieldsNames.returnStartPoint.title && startPointOfRecurring) {
        return current && current.valueOf() < DateHelper.getStartOfTheDay(startPointOfRecurring);
      }
    },
    [startPointOfRecurring],
  );

  const disabledDatesForPickUpDate = useCallback(
    (minDate) => disabledDate(datePickerFieldsNames.pickUpDate.title, minDate),
    [disabledDate],
  );

  const disabledDatesForAppointment = useCallback(


    (minDate) => disabledDate(datePickerFieldsNames.appointmentDate.title, minDate),
    [disabledDate],
  );

  const disabledDatesForReturnTime = useCallback(
    (minDate) => disabledDate(datePickerFieldsNames.returnTime.title, minDate),
    [disabledDate],
  );

  const disabledDatesForStartPointOfRecurring = useCallback(
    (minDate) => disabledDate(datePickerFieldsNames.startPoint.title, minDate),
    [disabledDate],
  );

  const disabledDatesForReturnTimeForRoundTripRecurring = useMemo(
    () => disabledDate(datePickerFieldsNames.returnStartPoint.title),
    [disabledDate],
  );

  const disabledDateForHigherLimit = useCallback(
    (d) => {
      if (!pickUpDateLowerLimit) {
        return false;
      }
      const maxDate = getFlexibleLatestPickupMax(pickUpDateLowerLimit);
      const minDate = pickUpDateLowerLimit;

      return (
        (minDate != null && DateHelper.isBefore(d, minDate) && !DateHelper.isTheSameDay(d, minDate)) ||
        (maxDate != null && DateHelper.isAfter(d, maxDate) && !DateHelper.isTheSameDay(d, maxDate))
      );
    },
    [pickUpDateLowerLimit, getFlexibleLatestPickupMax],
  );

  const disabledDateForReturnHigherLimit = useCallback(
    (minAvailableDate) => (d) => {
      if (!returnTimeLowerLimit) {
        return false;
      }
      const maxDate = getFlexibleLatestPickupMax(returnTimeLowerLimit);
      const minDate = DateHelper.isWithinInterval(d, { start: returnTimeLowerLimit, end: maxDate })
        ? returnTimeLowerLimit
        : minAvailableDate;

      return (
        (minDate != null && DateHelper.isBefore(d, minDate) && !DateHelper.isTheSameDay(d, minDate)) ||
        (maxDate != null && DateHelper.isAfter(d, maxDate) && !DateHelper.isTheSameDay(d, maxDate))
      );
    },
    [returnTimeLowerLimit, getFlexibleLatestPickupMax],
  );

  const disabledDateTimeForReturnHigherLimit = useCallback(
    (currentDate) => getFlexibleDisabledTime(returnTimeLowerLimit, currentDate),
    [getFlexibleDisabledTime, returnTimeLowerLimit],
  );

  const disabledTimeForHigherLimit = useCallback(
    (currentDate) => getFlexibleDisabledTime(pickUpDateLowerLimit, currentDate),
    [getFlexibleDisabledTime, pickUpDateLowerLimit],
  );

  const handleChangeTZ = useCallback(
    async ({ setFieldValue, name, value, values }) => {
      switch (name) {
        case datePickerFieldsNames.pickUpDate.timezone:
        case datePickerFieldsNames.returnTime.timezone:
          await setFieldValue(datePickerFieldsNames.pickUpDate.timezone, value);
          await setFieldValue(datePickerFieldsNames.returnTime.timezone, value);

          if (isAppointmentDateSelected) {
            await setFieldValue(datePickerFieldsNames.appointmentDate.timezone, value);
          } else {
            await setFieldValue(datePickerFieldsNames.appointmentDate.timezone, "");
          }

          await setFieldValue(datePickerFieldsNames.pickUpDate.datetime, "");

          // await setFieldValue(
          //   datePickerFieldsNames.appointmentDate.datetime,
          //   ""
          // );
          //await setAppointmentDate("");
          await setFieldValue(
            datePickerFieldsNames.pickUpDateLowerLimit.datetime,
            ""
          );
          await setFieldValue(datePickerFieldsNames.returnTime.datetime, "");
          await setFieldValue(
            datePickerFieldsNames.returnTimeLowerLimit.datetime,
            ""
          );

          await setPickUpDate(null);
          await setPickUpDateLowerLimit(null);
          await setAppointmentDate(null);
          await setReturnTimeDate(null);
          await setReturnTimeLowerLimit(null);
          await setFieldValue('appointmentDate.datetime', '');
          if (values?.isRecurring) {
            await setFieldValue(
              datePickerFieldsNames.startPoint.timezone,
              value
            );
            await setFieldValue(datePickerFieldsNames.startPoint.datetime, "");

            if (values?.isRoundtrip) {
              await setFieldValue(
                datePickerFieldsNames.returnStartPoint.timezone,
                value
              );
              await setFieldValue(
                datePickerFieldsNames.returnStartPoint.datetime,
                ""
              );
            }
          }

          await setStartPointOfRecurring(null);
          await setReturnStartPoint(null);
          break;

        case datePickerFieldsNames.startPoint.timezone:
          await setFieldValue(name, value);
          await setFieldValue(datePickerFieldsNames.startPoint.datetime, "");
          await setFieldTouched(
            datePickerFieldsNames.startPoint.datetime,
            true
          );
          await setStartPointOfRecurring(null);
          break;

        case datePickerFieldsNames.returnStartPoint.timezone:
          await setFieldValue(name, value);
          await setFieldValue(
            datePickerFieldsNames.returnStartPoint.datetime,
            ""
          );
          await setFieldTouched(
            datePickerFieldsNames.returnStartPoint.datetime,
            true
          );
          await setReturnStartPoint(null);
          break;

        case datePickerFieldsNames.appointmentDate.timezone:
          await setFieldValue(name, value);
          // await setFieldValue(datePickerFieldsNames.pickUpDate.timezone, value);
          // await setFieldValue(datePickerFieldsNames.returnTime.timezone, value);
          // await setFieldValue(
          //   datePickerFieldsNames.appointmentDate.datetime,
          //   ""
          //);
          await setAppointmentDate(null);
          await setFieldValue('appointmentDate.datetime', '');
          await setFieldTouched(
            datePickerFieldsNames.appointmentDate.datetime,
            true
          );
          await setReturnStartPoint(null);
          break;

        default:
          break;
      }
      await validateForm();
    },
    [setFieldTouched, validateForm],
  );

  const handleReccuringCheckbox = useCallback(
    async ({ name, checked, setFieldValue, values }) => {
      await setFieldValue(name, checked);

      if (!checked) {
        await setFieldValue('recurrenceRule', undefined);
        await setStartPointOfRecurring(null);
        await setReturnStartPoint(null);
      } else if (checked) {
        await setFieldValue(datePickerFieldsNames.startPoint.datetime, values?.pickUpDate?.datetime);
        await setFieldValue(datePickerFieldsNames.startPoint.timezone, values?.pickUpDate?.timezone);
        await setStartPointOfRecurring(pickUpDate);
        if (values?.isRoundtrip) {
          await setFieldValue(datePickerFieldsNames.returnStartPoint.datetime, values?.returnTime?.datetime);
          await setFieldValue(datePickerFieldsNames.returnStartPoint.timezone, values?.returnTime?.timezone);
          await setReturnStartPoint(returnTimeDate);
        }
      }
      await validateForm();
    },
    [validateForm, pickUpDate, returnTimeDate],
  );

  return {
    handlePickUpDateTime,
    handlePickUpDateOnSelect,
    handlePickUpLowerLimitOnSelect,
    handleReturnTimeOnSelect,
    handleReturnLowerLimitOnSelect,
    handleReturnTime,
    handleStartPointTime,
    handleReturnStartPoint,
    handleRoundTripCheckbox,
    disabledDatesForPickUpDate,
    disabledDatesForAppointment,
    disabledDatesForReturnTime,
    disabledDatesForStartPointOfRecurring,
    disabledDatesForReturnTimeForRoundTripRecurring,
    disabledDateTime,
    handleChangeTZ,
    pickUpDate,
    returnTimeDate,
    startPointOfRecurring,
    returnStartPoint,
    setStartPointOfRecurring,
    setReturnStartPoint,
    handleReccuringCheckbox,
    handleUseWindowOfTime,
    disabledDateForHigherLimit,
    disabledTimeForHigherLimit,
    handleChangePickUpDateToLowerLimit,
    pickUpDateLowerLimit,
    returnTimeLowerLimit,
    handleReturnTimeLowerLimit,
    disabledDateForReturnHigherLimit,
    disabledDateTimeForReturnHigherLimit,
    handleChangeappointmentDate,
    appointmentDate,
    handleAppointmentDateCheckBox,
  };
};
